import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import withTranslation from 'hoc/withTranslation';
import { setAvoidSegmentPicking, cancelAvoidSegmentPicking } from 'actions';
import { colors, fontSize } from '../../global/variables';

const { primaryTextColor, secondaryTextColor, primaryBorderColor, aqua } = colors;
const { small, smaller } = fontSize;

const DIRECTIONS = ['both', 'forward', 'backward'];

const StyledPopup = styled.div(({ dragPosition }) => ({
  position: 'absolute',
  top: dragPosition ? dragPosition.y : '6rem',
  left: dragPosition ? dragPosition.x : '50%',
  transform: dragPosition ? 'none' : 'translateX(-50%)',
  backgroundColor: '#fff',
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  boxShadow: '0 4px 16px rgba(15,22,33,.12)',
  padding: '0.75rem 0.9rem',
  width: '300px',
  zIndex: 1000,
}));

const StyledTitle = styled.div({
  fontSize: smaller,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: secondaryTextColor,
  marginBottom: '0.5rem',
  cursor: 'grab',
  userSelect: 'none',
});

const StyledSegmentId = styled.div({
  fontFamily: 'monospace',
  fontSize: small,
  color: primaryTextColor,
  backgroundColor: 'rgba(0,0,0,.025)',
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  padding: '0.3rem 0.5rem',
  marginBottom: '0.6rem',
  wordBreak: 'break-all',
});

const StyledHint = styled.div({
  fontSize: small,
  color: secondaryTextColor,
  marginBottom: '0.6rem',
});

const StyledError = styled.div({
  fontSize: small,
  color: colors.negativeTextColor,
  marginBottom: '0.6rem',
});

const StyledDirectionRow = styled.div({
  display: 'flex',
  gap: '0.3rem',
  marginBottom: '0.65rem',
});

const StyledDirectionPill = styled.button(({ active }) => ({
  flex: 1,
  background: active ? aqua : 'transparent',
  color: active ? '#fff' : primaryTextColor,
  border: `1px solid ${active ? aqua : primaryBorderColor}`,
  borderRadius: '1.5rem',
  padding: '0.25rem 0.4rem',
  fontSize: smaller,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'FiraGO',
}));

const StyledFooter = styled.div({
  display: 'flex',
  gap: '0.4rem',
});

const StyledButton = styled.button({
  flex: 1,
  fontFamily: 'FiraGO',
  fontSize: smaller,
  fontWeight: 500,
  padding: '0.35rem 0.5rem',
  borderRadius: '0.12rem',
  cursor: 'pointer',
});

const StyledCancelButton = styled(StyledButton)({
  background: 'transparent',
  color: primaryTextColor,
  border: `1px solid ${primaryBorderColor}`,
});

const StyledPrimaryButton = styled(StyledButton)({
  background: aqua,
  color: '#fff',
  border: 'none',
});

const StyledManualEntryToggle = styled.button({
  background: 'transparent',
  border: 'none',
  color: aqua,
  fontSize: smaller,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  padding: 0,
  marginBottom: '0.6rem',
});

const StyledManualInput = styled.input({
  width: '100%',
  fontFamily: 'monospace',
  fontSize: small,
  padding: '0.35rem 0.5rem',
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  marginBottom: '0.6rem',
});

const AvoidSegmentPickerPopup = ({
  translations: {
    wizard: {
      fleet: {
        avoidSegmentResolvedTitle,
        avoidSegmentPromptLabel,
        avoidSegmentResolvingLabel,
        avoidSegmentErrorLabel,
        avoidSegmentDirectionLabel,
        avoidSegmentDirectionLabels,
        avoidSegmentPickAnotherLabel,
        avoidSegmentAddLabel,
        avoidSegmentManualEntryToggleLabel,
        avoidSegmentIdPlaceholder,
        avoidAreaCancelLabel,
      },
    },
  },
}) => {
  const dispatch = useDispatch();
  const { active, status, segment, direction } = useSelector((state) => state.avoidSegmentPicking);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualId, setManualId] = useState('');
  const [dragPosition, setDragPosition] = useState(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!active) {
      setShowManualEntry(false);
      setManualId('');
      setDragPosition(null);
    }
  }, [active]);

  const onDragStart = useCallback((e) => {
    const popup = e.currentTarget.closest('#avoid-segment-picker-popup');
    const popupRect = popup.getBoundingClientRect();
    const parent = popup.offsetParent || document.body;
    const parentRect = parent.getBoundingClientRect();
    dragRef.current = {
      offsetX: e.clientX - popupRect.left,
      offsetY: e.clientY - popupRect.top,
      parentLeft: parentRect.left,
      parentTop: parentRect.top,
    };

    const onMouseMove = (moveEvent) => {
      const { offsetX, offsetY, parentLeft, parentTop } = dragRef.current;
      setDragPosition({
        x: moveEvent.clientX - offsetX - parentLeft,
        y: moveEvent.clientY - offsetY - parentTop,
      });
    };
    const onMouseUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const onSelectDirection = useCallback(
    (nextDirection) => dispatch(setAvoidSegmentPicking({ direction: nextDirection })),
    [dispatch],
  );
  const onPickAnother = useCallback(() => {
    setShowManualEntry(false);
    setManualId('');
    dispatch(setAvoidSegmentPicking({ status: 'idle', segment: null, error: null }));
  }, [dispatch]);
  const onCancel = useCallback(() => dispatch(cancelAvoidSegmentPicking()), [dispatch]);
  const onAddSegment = useCallback(
    () =>
      dispatch(
        setAvoidSegmentPicking({
          active: false,
          confirmed: { id: segment.id, direction, coordinates: segment.coordinates },
        }),
      ),
    [dispatch, segment, direction],
  );
  const onToggleManualEntry = useCallback(() => setShowManualEntry((prev) => !prev), []);
  const onManualIdChange = useCallback((e) => setManualId(e.target.value), []);
  const onAddManualSegment = useCallback(() => {
    if (!manualId.trim()) return;
    dispatch(
      setAvoidSegmentPicking({
        active: false,
        confirmed: { id: manualId.trim(), direction },
      }),
    );
  }, [dispatch, manualId, direction]);

  if (!active) return null;

  const isResolved = status === 'resolved' && segment;

  return (
    <StyledPopup id="avoid-segment-picker-popup" dragPosition={dragPosition}>
      <StyledTitle onMouseDown={onDragStart}>{avoidSegmentResolvedTitle}</StyledTitle>
      {status === 'idle' && !showManualEntry && <StyledHint>{avoidSegmentPromptLabel}</StyledHint>}
      {status === 'loading' && <StyledHint>{avoidSegmentResolvingLabel}</StyledHint>}
      {status === 'error' && <StyledError>{avoidSegmentErrorLabel}</StyledError>}
      {isResolved && (
        <StyledSegmentId>
          {segment.name ? `${segment.name} — ${segment.id}` : segment.id}
        </StyledSegmentId>
      )}
      {!isResolved && status !== 'loading' && !showManualEntry && (
        <StyledManualEntryToggle
          type="button"
          id="button-avoid-segment-manual-entry-toggle"
          onClick={onToggleManualEntry}
        >
          {avoidSegmentManualEntryToggleLabel}
        </StyledManualEntryToggle>
      )}
      {!isResolved && showManualEntry && (
        <StyledManualInput
          id="input-avoid-segment-manual-id"
          type="text"
          placeholder={avoidSegmentIdPlaceholder}
          value={manualId}
          onChange={onManualIdChange}
        />
      )}
      {(isResolved || showManualEntry) && (
        <>
          <StyledHint>{avoidSegmentDirectionLabel}</StyledHint>
          <StyledDirectionRow>
            {DIRECTIONS.map((dir) => (
              <StyledDirectionPill
                key={dir}
                type="button"
                id={`button-avoid-segment-direction-${dir}`}
                active={direction === dir}
                onClick={() => onSelectDirection(dir)}
              >
                {avoidSegmentDirectionLabels[dir]}
              </StyledDirectionPill>
            ))}
          </StyledDirectionRow>
        </>
      )}
      <StyledFooter>
        {isResolved ? (
          <>
            <StyledCancelButton
              type="button"
              id="button-avoid-segment-pick-another"
              onClick={onPickAnother}
            >
              {avoidSegmentPickAnotherLabel}
            </StyledCancelButton>
            <StyledPrimaryButton type="button" id="button-avoid-segment-add" onClick={onAddSegment}>
              {avoidSegmentAddLabel}
            </StyledPrimaryButton>
          </>
        ) : showManualEntry ? (
          <>
            <StyledCancelButton
              type="button"
              id="button-avoid-segment-manual-cancel"
              onClick={onCancel}
            >
              {avoidAreaCancelLabel}
            </StyledCancelButton>
            <StyledPrimaryButton
              type="button"
              id="button-avoid-segment-manual-add"
              onClick={onAddManualSegment}
            >
              {avoidSegmentAddLabel}
            </StyledPrimaryButton>
          </>
        ) : (
          <StyledCancelButton type="button" id="button-avoid-segment-cancel" onClick={onCancel}>
            {avoidAreaCancelLabel}
          </StyledCancelButton>
        )}
      </StyledFooter>
    </StyledPopup>
  );
};

export default withTranslation(AvoidSegmentPickerPopup);
